import React from 'react'
import dayjs from 'dayjs'
import { useMutation } from '@tanstack/react-query'
import { Button, InfoBox } from '@mobi/component-library/Common/V2'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import type { PrecommitmentResponse } from '@mobi/account/Areas/SignUp/Utils/SignUpApi'
import { DEFAULT_COOLING_OFF_PERIOD_DAYS } from './constants'
import { precommitmentUrl } from '@mobi/account/Utils'
import { post } from '@mobi/account/Utils'
import { trackBlockDebitCard, trackUnblockDebitCard, trackCancelUnblockDebitCard } from './Utils/ga'
import { Modal } from '@mobi/component-library/Overlays/Modal'
import {
  BlockDebitCardFormStyled,
  ModalContentStyled,
  ParagraphStyled,
} from './BlockDebitCard.styles'

export const BankCardBlock = ({
  data,
  hasDataFetchFailed,
}: {
  data: PrecommitmentResponse | undefined
  hasDataFetchFailed: boolean
}): JSX.Element => {
  const [isPopupShowing, setIsPopupShowing] = React.useState(false)
  const [precommitmentDetails, setPrecommitmentDetails] = React.useState(data)

  React.useEffect(() => {
    setPrecommitmentDetails(data)
  }, [data])

  const isDebitCardDepositsBlocked = precommitmentDetails?.BlockCreditCardDeposits
  const isDebitCardUnblockPendingCoolingOff = !!precommitmentDetails?.ProposedCreditCardReleaseDate
  const isCancellingUnblock = isDebitCardDepositsBlocked && isDebitCardUnblockPendingCoolingOff

  return (
    <BlockDebitCardFormStyled>
      <ParagraphStyled>
        You can choose to block the use of debit cards on your account.
      </ParagraphStyled>
      {isPopupShowing && (
        <CardBlockPopup
          setIsPopupShowing={setIsPopupShowing}
          setPrecommitmentDetails={setPrecommitmentDetails}
          precommitmentDetails={precommitmentDetails}
        />
      )}
      {hasDataFetchFailed && (
        <InfoBox color='orange' size='sm'>
          Unable to display your debit card status. Please try again later.
        </InfoBox>
      )}
      {!hasDataFetchFailed && (
        <>
          <InfoBox
            color={precommitmentDetails?.BlockCreditCardDeposits ? 'orange' : 'blue'}
            size='sm'
          >
            {isDebitCardDepositsBlocked
              ? isDebitCardUnblockPendingCoolingOff
                ? `Your debit card block will be removed on ${precommitmentDetails?.ProposedCreditCardReleaseDate}`
                : 'Debit card deposits are currently blocked'
              : 'Debit card deposits are not currently blocked'}
          </InfoBox>
          <Button
            id='credit-card-block-button'
            color='primary'
            onClick={() => {
              if (isCancellingUnblock) {
                trackCancelUnblockDebitCard()
              }
              setIsPopupShowing(true)
            }}
            isFullWidth
          >
            {isDebitCardDepositsBlocked
              ? isDebitCardUnblockPendingCoolingOff
                ? 'Cancel debit card unblock'
                : 'Remove debit card block'
              : 'Block debit card deposits'}
          </Button>
        </>
      )}
    </BlockDebitCardFormStyled>
  )
}

type CardBlockPopupProps = {
  setIsPopupShowing: (x: boolean) => void
  setPrecommitmentDetails: (x: PrecommitmentResponse) => void
  precommitmentDetails?: PrecommitmentResponse
}

const CardBlockPopup: React.FC<CardBlockPopupProps> = ({
  setIsPopupShowing,
  setPrecommitmentDetails,
  precommitmentDetails,
}) => {
  const isRemovingBlock =
    precommitmentDetails?.BlockCreditCardDeposits &&
    !precommitmentDetails.ProposedCreditCardReleaseDate

  const mutation = useMutation({
    mutationFn: () =>
      post<boolean, PrecommitmentResponse>(
        precommitmentUrl.updateCreditCardDeposits,
        !isRemovingBlock
      ),
    onSuccess: data => {
      if (isRemovingBlock) {
        trackUnblockDebitCard()
      } else {
        trackBlockDebitCard()
      }
      setIsPopupShowing(false)
      setPrecommitmentDetails(data)
    },
  })

  const headerTitle = isRemovingBlock ? 'Remove Debit Card Block' : 'Block Debit Card Deposits'
  const headerSubtitle = isRemovingBlock ? (
    <>
      Your debit card block will be removed on{' '}
      <strong>
        {dayjs()
          .add(
            precommitmentDetails?.CoolingOffPeriodDays || DEFAULT_COOLING_OFF_PERIOD_DAYS,
            'days'
          )
          .format('DD/MM/YYYY')}
      </strong>
      , after the mandatory cooling off period.
    </>
  ) : (
    <>
      I understand that debit card deposits to my account will be blocked effective immediately. If
      I choose to remove this block in future, it will be subject to a{' '}
      {precommitmentDetails?.CoolingOffPeriodDays || DEFAULT_COOLING_OFF_PERIOD_DAYS} day cooling
      off period.
    </>
  )

  return (
    <Modal
      headerIcon='warning'
      headerIconAlignment='center'
      headerTitle={headerTitle}
      headerSubtitle={<ModalContentStyled>{headerSubtitle}</ModalContentStyled>}
      headerTextAlignment='center'
      dismissStatus='disallowed'
      footerComponent={
        <>
          <Button
            id='credit-card-block-cancel'
            isFullWidth
            color='secondary_grey'
            onClick={() => setIsPopupShowing(false)}
          >
            Cancel
          </Button>

          <Button
            id='credit-card-block-save'
            isFullWidth
            color='primary'
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Confirm
          </Button>
        </>
      }
      isOpen
    >
      {mutation.isPending && <Spinner />}
    </Modal>
  )
}
